import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChartConfiguration } from "chart.js";
import ExcelJS from "exceljs";
import { useDarkMode } from "../../../../../context/ThemeContext";
import { getThemeClasses } from "../../../shared";
import { useAlert } from "../../../../../context/AlertContext";
import { useCompaniesStore } from "../../../../../store/admin/companiesStore";
import { useAllUnits } from "../../../../../features/units/queries";
import { ReportChart, ReportStatCard, CHART_PALETTE, gridColor, tickColor, reportLegend } from "../../../../../pages/reports/components/ReportCharts";
import ReportDateRangePicker from "../../../../../pages/reports/components/ReportDateRangePicker";
import { toLocalInputValue } from "../../../../../pages/reports/utils/reportDateFormat";
import {
  CATEGORIES,
  CATEGORY_LABEL,
  TEMPLATE_HEADERS,
  apiMessage,
  deleteFleetCost,
  fetchFleetCosts,
  importErrors,
  importFleetCosts,
  pctChange,
  rowsFromSheet,
  type FleetCost,
} from "../../../../../features/fleetCosts/fleetCosts";
import FleetCostModal from "./FleetCostModal";

const DAY_MS = 24 * 60 * 60 * 1000;
const money = (n: number) => n.toLocaleString("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 });

function Delta({ value }: { value: number | null }) {
  if (value == null) return null;
  // En costos, subir es malo: rojo; bajar, verde.
  const up = value > 0;
  return (
    <span className={`text-xs font-semibold ${up ? "text-red-500" : "text-green-600"}`}>
      {up ? "+" : ""}
      {value.toFixed(0)} % {up ? "↑" : "↓"}
    </span>
  );
}

export default function FleetCostsSection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const { showAlert } = useAlert();
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const { data: units = [] } = useAllUnits();

  const [start, setStart] = useState(() => toLocalInputValue(new Date(Date.now() - 30 * DAY_MS)));
  const [end, setEnd] = useState(() => toLocalInputValue(new Date()));
  const [vehicleId, setVehicleId] = useState("");
  const [editing, setEditing] = useState<FleetCost | "new" | null>(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const from = new Date(start).toISOString();
  const to = new Date(end).toISOString();
  const queryKey = ["fleet-costs", companyId, from, to, vehicleId];
  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () => fetchFleetCosts(companyId!, from, to, vehicleId || null),
    enabled: !!companyId && !!start && !!end,
    staleTime: 60_000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["fleet-costs", companyId] });

  const unitName = useMemo(() => {
    const map: Record<string, string> = {};
    (units as any[]).forEach((u) => {
      map[u.id] = [u.name, u.plate].filter(Boolean).join(" · ") || u.id;
    });
    return map;
  }, [units]);

  const s = data?.summary;
  const p = data?.previous;
  const topVehicle = s ? Object.entries(s.byVehicle).sort((a, b) => b[1] - a[1])[0] : undefined;

  const donut = useMemo<ChartConfiguration | null>(() => {
    if (!s || s.total === 0) return null;
    const cats = CATEGORIES.filter((c) => s.byCategory[c]);
    return {
      type: "doughnut",
      data: {
        labels: cats.map((c) => `${CATEGORY_LABEL[c]}: ${((s.byCategory[c]! / s.total) * 100).toFixed(1)} %`),
        datasets: [{ data: cats.map((c) => s.byCategory[c]!), backgroundColor: cats.map((_, i) => CHART_PALETTE[i % CHART_PALETTE.length]), borderWidth: 0 }],
      },
      options: { responsive: true, maintainAspectRatio: false, cutout: "62%", plugins: { legend: { ...reportLegend(isDarkMode), position: "right" } } },
    } as ChartConfiguration;
  }, [s, isDarkMode]);

  const bars = useMemo<ChartConfiguration | null>(() => {
    if (!s || s.total === 0) return null;
    const months = Object.keys(s.byMonth).sort();
    return {
      type: "bar",
      data: { labels: months, datasets: [{ label: "Gasto", data: months.map((m) => s.byMonth[m]), backgroundColor: CHART_PALETTE[0], borderRadius: 6 }] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { color: tickColor(isDarkMode) }, grid: { display: false } },
          y: { ticks: { color: tickColor(isDarkMode), callback: (v) => money(Number(v)) }, grid: { color: gridColor(isDarkMode) } },
        },
      },
    };
  }, [s, isDarkMode]);

  const onDelete = (c: FleetCost) =>
    showAlert({
      title: "¿Borrar gasto?",
      text: `Se borrará el gasto de ${money(c.amount)}${c.recurrence ? " y todas sus repeticiones" : ""}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Borrar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      onConfirm: async () => {
        try {
          await deleteFleetCost(companyId!, c.id);
          refresh();
        } catch (e) {
          showAlert({ title: "No se borró", text: apiMessage(e, "No se pudo borrar el gasto."), icon: "error", confirmButtonText: "OK", confirmButtonColor: "#2563eb", showCancelButton: false });
        }
      },
    });

  const downloadTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Gastos");
    ws.addRow(TEMPLATE_HEADERS);
    const first = (units as any[])[0];
    ws.addRow([first?.plate || first?.name || "27BK8R", "combustible", "Diésel", 1234.5, "05/10/2026", "", ""]);
    ws.addRow([first?.plate || first?.name || "27BK8R", "servicio", "Seguro", 24000, "15/04/2026", "anual", "Póliza actualizada"]);
    ws.getRow(1).font = { bold: true };
    ws.columns.forEach((c) => (c.width = 18));
    const buf = await wb.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla_costes_flota.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onImportFile = async (file: File) => {
    setImporting(true);
    try {
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(await file.arrayBuffer());
      const ws = wb.worksheets[0];
      const sheet: unknown[][] = [];
      ws?.eachRow({ includeEmpty: false }, (row) => {
        const values = (row.values as unknown[]).slice(1); // exceljs indexa desde 1
        sheet.push(values.map((v) => (v && typeof v === "object" && "text" in (v as object) ? (v as { text: string }).text : v)));
      });
      const rows = rowsFromSheet(sheet);
      if (rows.length === 0) throw new Error("El archivo no tiene filas de gastos.");
      const res = await importFleetCosts(companyId!, rows);
      showAlert({ title: "Gastos importados", text: `Se registraron ${res.data?.data?.data?.created ?? rows.length} gastos.`, icon: "success", confirmButtonText: "OK", confirmButtonColor: "#2563eb", showCancelButton: false });
      refresh();
    } catch (e) {
      const errs = importErrors(e);
      const text = errs.length
        ? `No se importó nada. Corrige estas filas:\n${errs.slice(0, 8).map((x) => `Fila ${x.row}: ${x.errors.join("; ")}`).join("\n")}${errs.length > 8 ? `\n…y ${errs.length - 8} más` : ""}`
        : apiMessage(e, (e as Error)?.message || "No se pudo leer el archivo.");
      showAlert({ title: "Revisa el archivo", text, icon: "warning", confirmButtonText: "OK", confirmButtonColor: "#2563eb", showCancelButton: false });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const muted = themeClasses.textSecondary;
  const card = isDarkMode ? "bg-dark-card border-dark-border" : "bg-white border-gray-200";

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4 flex flex-col gap-4">
      <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-3 rounded-lg border p-3 ${card}`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <div className="flex flex-col gap-1">
            <label className={`text-xs ${themeClasses.text}`}>Periodo</label>
            <ReportDateRangePicker startValue={start} endValue={end} onChangeStart={setStart} onChangeEnd={setEnd} />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="costs-unit" className={`text-xs ${themeClasses.text}`}>Unidad</label>
            <select
              id="costs-unit"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              className={`rounded-md border px-2 py-2 text-sm min-w-52 ${isDarkMode ? "bg-white/5 border-dark-border text-white" : "bg-white border-gray-300"}`}
            >
              <option value="">Todas las unidades</option>
              {(units as any[]).map((u) => (
                <option key={u.id} value={u.id}>
                  {unitName[u.id]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={downloadTemplate} className={`text-sm px-3 py-2 rounded-md border ${isDarkMode ? "border-dark-border text-white" : "border-gray-300"}`}>
            <i className="ri-file-excel-2-line mr-1" />Plantilla
          </button>
          <button
            type="button"
            disabled={importing || !companyId}
            onClick={() => fileRef.current?.click()}
            className={`text-sm px-3 py-2 rounded-md border disabled:opacity-50 ${isDarkMode ? "border-dark-border text-white" : "border-gray-300"}`}
          >
            <i className="ri-upload-2-line mr-1" />
            {importing ? "Importando…" : "Importar Excel"}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files?.[0] && onImportFile(e.target.files[0])} />
          <button type="button" disabled={!companyId} onClick={() => setEditing("new")} className="text-sm px-4 py-2 rounded-md bg-primary-500 text-white disabled:opacity-50">
            <i className="ri-add-line mr-1" />Agregar gasto
          </button>
        </div>
      </div>

      {!companyId && <p className={`text-sm ${muted}`}>Selecciona una compañía para ver sus costes.</p>}
      {isError && <p className="text-sm text-red-500">{apiMessage(error, "No se pudieron cargar los costes.")}</p>}
      {isLoading && <p className={`text-sm ${muted}`}>Cargando costes…</p>}

      {s && p && (
        <>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: "Costo total", value: money(s.total), delta: pctChange(s.total, p.total), sub: `Periodo anterior: ${money(p.total)}` },
              { label: "Costo por día", value: money(s.perDay), delta: pctChange(s.perDay, p.perDay), sub: `Periodo anterior: ${money(p.perDay)}` },
              { label: "Costo por km", value: s.perKm != null ? money(s.perKm) : "—", delta: null, sub: s.usage ? `${s.usage.km.toLocaleString("es-MX")} km recorridos` : "Sin datos de recorrido" },
              { label: "Costo por hora de motor", value: s.perHour != null ? money(s.perHour) : "—", delta: null, sub: s.usage ? `${s.usage.engineHours.toLocaleString("es-MX")} h de motor encendido` : "Sin datos de recorrido" },
              { label: "Gastos registrados", value: String(s.count), delta: null, sub: `${s.days} días` },
              { label: "Unidad con más gasto", value: topVehicle ? money(topVehicle[1]) : "—", delta: null, sub: topVehicle ? unitName[topVehicle[0]] || topVehicle[0] : "Sin gastos" },
            ].map((c) => (
              <div key={c.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs ${muted}`}>{c.label}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <p className={`text-xl font-semibold tabular-nums ${themeClasses.text}`}>{c.value}</p>
                  <Delta value={c.delta} />
                </div>
                <p className={`text-xs mt-1 truncate ${muted}`}>{c.sub}</p>
              </div>
            ))}
          </div>

          {s.total > 0 ? (
            <div className="grid gap-3 lg:grid-cols-2">
              <div className={`min-w-0 rounded-xl border p-3 ${card}`}>
                <p className={`text-sm font-semibold mb-2 ${themeClasses.text}`}>Por categoría</p>
                {donut && <ReportChart config={donut} height={240} />}
              </div>
              <div className={`min-w-0 rounded-xl border p-3 ${card}`}>
                <p className={`text-sm font-semibold mb-2 ${themeClasses.text}`}>Por mes</p>
                {bars && <ReportChart config={bars} height={240} />}
              </div>
            </div>
          ) : (
            <ReportStatCard icon="ri-money-dollar-circle-line" label="Sin gastos en el periodo" value="Agrega uno o importa tu Excel" isDarkMode={isDarkMode} />
          )}

          {data!.items.length > 0 && (
            <div className="w-full overflow-auto rounded-lg shadow max-h-[60vh]">
              <table className="w-full text-xs">
                <thead className="bg-primary-500 text-white sticky top-0 z-10">
                  <tr>
                    {["Fecha", "Unidad", "Categoría", "Concepto", "Monto", "Recurrente", "Nota", ""].map((h) => (
                      <th key={h} className={`px-3 py-2 font-bold whitespace-nowrap ${h === "Monto" ? "text-right" : "text-left"}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data!.items.map((c) => (
                    <tr key={`${c.id}-${c.occurrence}`} className={`border-b ${themeClasses.cardBorder} ${themeClasses.text}`}>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{new Date(c.occurrence).toLocaleDateString("es-MX")}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{unitName[c.vehicle_id] || c.vehicle_id}</td>
                      <td className="px-3 py-2">{CATEGORY_LABEL[c.category]}</td>
                      <td className="px-3 py-2">{c.concept || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-right">{money(c.amount)}</td>
                      <td className="px-3 py-2">{c.recurrence === "month" ? "Mensual" : c.recurrence === "year" ? "Anual" : "No"}</td>
                      <td className={`px-3 py-2 max-w-56 truncate ${muted}`} title={c.note}>{c.note || ""}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button type="button" aria-label="Editar gasto" onClick={() => setEditing(c)} className={`px-2 ${muted} hover:text-primary-500`}>
                          <i className="ri-edit-line" />
                        </button>
                        <button type="button" aria-label="Borrar gasto" onClick={() => onDelete(c)} className={`px-2 ${muted} hover:text-red-500`}>
                          <i className="ri-delete-bin-line" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {editing && companyId && (
        <FleetCostModal
          companyId={companyId}
          cost={editing === "new" ? null : editing}
          units={(units as any[]).map((u) => ({ id: u.id, label: unitName[u.id] }))}
          isDarkMode={isDarkMode}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
